import { IS_CUSTOMER_KEY, ROLES_KEY } from "./authUtils";

interface DynamicRoute {
  pattern: RegExp;
  getTitle: (agentName: string) => string;
}

const getQofileCheck = () => {
  const customerLevel = localStorage.getItem("customerLevel");
  const userRole = localStorage.getItem("roles");
  let check = false;
  if (userRole === "agent") {
    return "Agent Profile";
  }
  if (
    customerLevel === "first" &&
    (userRole === "admin" || userRole === "customersuperadmin")
  ) {
    check = true;
  } else if (
    customerLevel === "second" &&
    (userRole === "admin" || userRole === "customersuperadmin")
  ) {
    check = true;
  } else if (customerLevel === "third" && userRole === "customer") {
    check = true;
  }
  if (check) {
    return "Profile";
  } else {
    return "Profile";
  }
};

/**
 * A mapping of static routes to their corresponding page titles.
 * This object is used to determine the title of a page based on the current route.
 */
const staticRoutes: { [key: string]: string } = {
  "/": "Dashboard",
  "/users": "Site Users",
  "/users/add": "Create User",
  "/user/profile": getQofileCheck(),
  "/email-notification-permissions": "Email Notification Permissions",
  "/change-password": "Change Password",
  // Customer
  "/customers": "Customer List",
  "/customer-markup": "Customer Markup List",
  "/customer-markup/add": "Customer Markup",
  "/customers/add": "Create Customer",
  "/customer-users": "Customer Users",
  "/customer-users/add": "Add Customer User",
  "/customer-items": "Customer Items",
  "/customer-items/add": "Add Customer Items",
  "/customers-doc": "Customer Doc List",
  "/customers-doc/add": "Add Document",
  "/customer-address": "Address",
  "/customer/rate": "Customer Rate",
  "/customers/additional-charges/add": "Add Customer Additional Charges",
  "/customers/group-invoice-list/add": "Add Customer Invoice Group",
  // Agent Management
  "/agent-list": "Agent List",
  "/agent-request": "Agent Requests",
  "/agent-request/add": "Add Agent",
  "/master-agent-list": "Master Agent List",
  "/agent-list/add": "Add Agent",
  "/agent-additional-charges": "Agent Additional Charges",
  "/agent-hubs": "Agent Hubs",
  "/agent-hubs/add": "Add Agent Hubs",
  "/agent-doc-list": "Agent Doc List",
  "/agent-doc-list/add": "Add Document",
  "/agent-list/additional-charges/add": "Add Agent Additional Charges",
  //Rep Management
  "/rep-list": "My Rep List",
  "/rep-list/add": "Add New Sales Rep",
  //Agent Rate
  "/agent-rates": "Agent Rates",
  "/agent-rates/add": "Create Agent Rate",
  "/agent-mile-zone": "Agent Mile Zone",
  "/agent-mile-zone/add": "Agent Mile Zone",
  "/agent-weight-zone": "Agent Weight Zone",
  "/agent-weight-zone/add": "Agent Weight Zone",
  "/fsc-agent-list": "FSC Agent List",
  "/fsc-agent-list/add": "Add FSC Agent",
  //Order Management
  "/customer-order": "Add Customer Order",
  "/quick-quote": "Quick Quote",
  "/all-orders": "All Orders",
  "/active-order": "Active Orders",
  "/delivered-order": "Delivered Orders",
  "/invoiced-order": "Invoiced Orders",
  "/finalized-order": "Finalized Orders",
  "/cancelled-order": "Cancelled Orders",
  "/dispatch-board-list": "Dispatch Board List",
  "/billed-order-history": "Customer Billed Order History List",
  "/saved-quotes": "Saved Quotes",
  // Agent API
  "/agent-api": "Agent API",
  "/agent-api/add": "Agent API",
  // Api Management
  "/Xcelarator-api": "XceleratorAPI List",
  "/Xcelarator-api/add": "Add XceleratorAPI",
  // Account Management
  "/group-invoice-list": "Customer Invoice Group List",
  "/group-invoice-list/add": "Add Customer Invoice Group",
  "/invoice-list": "Order Invoice List",
  "/invoice-list/add": "Add Order Invoice",
  "/finalized-orders": "Finalized Orders",
  //Report
  "/create-report": "Create Report",
  "/saved-reports": "Saved Reports",
  "/audit-log": "Audit Log",
  //Settings
  "/settings-additional-charges": "Additional Charges List",
  "/settings-additional-charges/add": "Add Additional Charges",
  //System Configuration
  "/system-configuration/services": "Services",
  "/system-configuration/onward-service": "Services",
  "/system-configuration/order-status": "Order Status",
  "/system-configuration/package-type": "Package Type",
  "/system-configuration/vehicle": "Vehicle Type",
  "/system-configuration/email-template": "Email Template",
  "/system-configuration/add-email-template": "Add Email Template",
  "/bulk-quote-listing": "Bulk Quote Listing",
  "/bulk-order-listing": "Bulk Order Listing",
  "/bulk-order-import": "Bulk Order Import",
  "/bulk-quote-import": "Bulk Quote Import",
  "/customer-address-book": "Address Book",
  "/customer-address-book/add": "Address Book",
  "/system-configuration/scac": "SCAC",
  "/system-configuration/emailConfig": "Email Configuration",
  "/system-configuration/region": "Region",
  "/system-configuration/fsc-location": "FSC Location",
  "/system-configuration/zip-code-list": "Zipcodes"
};

/**
 * An array of dynamic routes that require a more complex pattern match.
 * Each route is associated with a function that returns the appropriate page title.
 */
const dynamicRoutes: DynamicRoute[] = [
  { pattern: /^\/users\/edit\/[^/]+$/, getTitle: () => "Edit User" },
  { pattern: /^\/rep-list\/edit\/[^/]+$/, getTitle: () => "Edit Sales Rep" },
  // Customer
  { pattern: /^\/customers\/edit\/[^/]+$/, getTitle: () => "Edit Customer" },
  {
    pattern: /^\/customer-users\/edit\/[^/]/,
    getTitle: () => "Edit Customer User",
  },
  {
    pattern: /^\/customer-items\/edit\/[^/]/,
    getTitle: () => "Edit Customer Items",
  },
  {
    pattern: /^\/customers-doc\/edit\/[^/]+$/,
    getTitle: () => "Edit Document",
  },
  {
    pattern: /^\/customer-markup\/edit\/[^/]+$/,
    getTitle: (agentName) => `Customer Markup - ${agentName}`,
  },
  {
    pattern: /^\/customers\/customers\/[^/]/,
    getTitle: (agentName) => `Customer List - ${agentName}`,
  },
  {
    pattern: /^\/customers\/customers\/[^/]/,
    getTitle: (agentName) => `Customer List - ${agentName}`,
  },
  {
    pattern: /^\/customer\/rate\/[^/]/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Rate"),
  },
  {
    pattern: /^\/customers\/additional-charges\/edit\/[^/]+$/,
    getTitle: () => "Edit Customer Additional Charges",
  },
  {
    pattern: /^\/customers\/group-invoice-list\/edit\/[^/]+$/,
    getTitle: () => "Edit Customer Invoice Group",
  },
  // Agent Management
  { pattern: /^\/agent-list\/edit\/[^/]+$/, getTitle: () => "Edit Agent" },
  { pattern: /^\/agent-request\/edit\/[^/]+$/, getTitle: () => "Edit Agent" },
  {
    pattern: /^\/agent-list\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Agent List"),
  },
  {
    pattern: /^\/master-agent-list\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Master Agent List"),
  },
  { pattern: /^\/agent-hubs\/edit\/[^/]+$/, getTitle: () => "Edit Agent Hubs" },
  {
    pattern: /^\/agent-doc-list\/edit\/[^/]+$/,
    getTitle: () => "Edit Document",
  },
  // Agent Rate
  {
    pattern: /^\/agent-rates\/edit\/[^/]+$/,
    getTitle: () => "Edit Agent Rate",
  },
  {
    pattern: /^\/agent-mile-zone\/edit\/[^/]+$/,
    getTitle: (agentName) => `Edit Agent Mile Zone - ${agentName}`,
  },
  {
    pattern: /^\/agent-weight-zone\/edit\/[^/]+$/,
    getTitle: (agentName) => `Edit Agent Weight Zone - ${agentName}`,
  },
  {
    pattern: /^\/fsc-agent-list\/edit\/[^/]+$/,
    getTitle: (agentName) => `Edit FSC Agent - ${agentName}`,
  },
  {
    pattern: /^\/agent-list\/additional-charges\/edit\/[^/]+$/,
    getTitle: () => "Edit Agent Additional Charges",
  },
  //Order Management
  {
    pattern: /^\/all-orders\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/active-order\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/delivered-order\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/invoiced-order\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/finalized-order\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/cancelled-order\/[^/]+$/,
    getTitle: (agentName) => (agentName ? agentName : "Customer Order History"),
  },
  {
    pattern: /^\/dispatch-board-list\/[^/]+$/,
    getTitle: () => "Dispatch Board List",
  },
  {
    pattern: /^\/billed-order-history\/view\/[^/]+$/,
    getTitle: (agentName) =>
      agentName !== "undefined" ? agentName : "View Order Details",
  },
  {
    pattern: /^\/Xcelarator-api\/edit\/[^/]+$/,
    getTitle: () => "Edit XceleratorAPI",
  },
  {
    pattern: /^\/Xcelarator-api\/agent\/[^/]+$/,
    getTitle: () => "Add XceleratorAPI",
  },
  {
    pattern: /^\/agent-api\/edit\/[^/]+$/,
    getTitle: (agentName) => `Agent API - ${agentName}`,
  },
  // Account Management
  {
    pattern: /^\/group-invoice-list\/edit\/[^/]+$/,
    getTitle: () => "Edit Customer Invoice Group",
  },
  {
    pattern: /^\/invoice-list\/edit\/[^/]+$/,
    getTitle: () => "Edit Order Invoice",
  },
  // Settings
  {
    pattern: /^\/settings-additional-charges\/edit\/[^/]+$/,
    getTitle: () => "Edit Additional Charges",
  },
  {
    pattern: /^\/system-configuration\/edit-email-template\/[^/]+$/,
    getTitle: () => "Edit Email Template",
  },
  {
    pattern: /^\/customer-address-book\/edit\/[^/]/,
    getTitle: () => "Edit Address Book",
  },
];

/**
 * Retrieves the title of a page based on the current route.
 *
 * @param {string} pathname - The current URL pathname.
 * @param {string} agentName - The name of the agent, if applicable.
 * @returns {string} - The title of the page.
 */
export const getTitleByRoute = (
  pathname: string,
  agentName: string
): string => {
  if (pathname === "/user/profile") {
    return getQofileCheck();
  }

  // Check static routes first
  if (staticRoutes[pathname]) {
    return staticRoutes[pathname];
  }

  // Check dynamic routes
  for (const route of dynamicRoutes) {
    if (route.pattern.test(pathname)) {
      return route.getTitle(agentName);
    }
  }

  // Default title
  return "Dashboard";
};
